import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  TablePagination,
  Snackbar,
  Chip,
  Divider,
  capitalize,
  Container,
  InputAdornment,
  Stack,
  Slide
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';

import {
  ShoppingCart as OrderIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';
import { User } from '../../types/auth';
import { UserDetails } from '../../types/user';
import { useLocalization } from '../../contexts/LocalizationContext';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useLocation } from 'react-router-dom';

const UserManagement: React.FC = () => {
  const { t } = useLocalization();
  const [ users, setUsers ] = useState<User[]>( [] );
  const [ searchTerm, setSearchTerm ] = useState( '' );

  const [ selectedUser, setSelectedUser ] = useState<UserDetails | null>( null );
  const [ openDialog, setOpenDialog ] = useState( false );
  const [ dialogType, setDialogType ] = useState<'view' | 'edit' | 'sales' | 'purchases' | 'delete' | null>( null );
  const [ isDeleting, setIsDeleting ] = useState( false );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ page, setPage ] = useState( 0 );
  const [ rowsPerPage, setRowsPerPage ] = useState( 10 );
  const [ totalUsers, setTotalUsers ] = useState( 0 );
  const [ loading, setLoading ] = useState( false );
  const [ allUsers, setAllUsers ] = useState<User[]>( [] ); // Store all users for local search
  const navigate = useNavigate();
  const [ statusDialogOpen, setStatusDialogOpen ] = useState( false );
  const [ userToUpdate, setUserToUpdate ] = useState<User | null>( null );

  const location = useLocation();
  const initialFilter = location.state?.filter || 'all';
  const [ statusFilter, setStatusFilter ] = useState<string>( initialFilter );

  const fetchUsers = async ( page: number, limit: number, search: string = '' ) => {
    try {
      if ( search.length > 0 && search.length < 3 ) {
        return; // ⛔ 1-2 letter par API call band
      }

      setLoading( true );

      const response = await userService.getUsers( {
        page: page + 1, // backend 1-based pagination use karta hai
        limit,
        status: statusFilter,
        search: search.length >= 3 ? search : "",

      } );

      // Type assertion (assuming same structure as OffersResponse)
      const data = response

      if ( data.success ) {
        // ❗ If both current users & API result are empty → do nothing
        if ( users.length === 0 && data.data.length === 0 ) {
          setLoading( false );
          return;
        }

        setUsers( data.data );
        setTotalUsers( data.pagination.total );
      }
      else {
        // handle API failure
        setToast( {
          open: true,
          message: data.message || t.users.fetch.error,
          severity: 'error',
        } );
      }
    } catch ( error ) {
      console.error( 'Failed to fetch users:', error );
      setToast( {
        open: true,
        message: t.users.fetch.error,
        severity: 'error',
      } );
    } finally {
      setLoading( false );
    }
  };

  useEffect( () => {
    fetchUsers( page, rowsPerPage, searchTerm );
  }, [ page, rowsPerPage, searchTerm, statusFilter ] );
  // Only fetch on initial load

  const handleSearchChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const newSearchTerm = event.target.value;
    setSearchTerm( newSearchTerm );
    setPage( 0 ); // Reset to first page when searching
    fetchUsers( 0, rowsPerPage, newSearchTerm );
  };

  const handleStatusChange = ( event: SelectChangeEvent ) => {
    const newStatus = event.target.value;
    setStatusFilter( newStatus );
    setPage( 0 ); // Reset to first page when changing status

  };

  const handleToggleStatus = async ( user: User ) => {
    try {
      setLoading( true );
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      const response = await userService.toggleUserStatus( { userId: user.id.toString(), status: newStatus } );
      if ( response.success ) {
        // Update the user's status in the local state
        setUsers( users.map( u =>
          u.id === user.id
            ? { ...u, status: newStatus }
            : u
        ) );
        setAllUsers( allUsers.map( u =>
          u.id === user.id
            ? { ...u, status: newStatus }
            : u
        ) );
        setToast( {
          open: true,
          message: response.message,
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || 'Failed to update user status',
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Failed to toggle user status:', error );
      setToast( {
        open: true,
        message: 'Failed to update user status',
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleViewUser = ( user: User ) => {
    const userDetails: UserDetails = {
      ...user,
      countryCode: user.countryCode || '',
      phoneNumber: user.phoneNumber || '',
      name: `${ user.firstName } ${ user.lastName }`,
      role: 'user', // Default role since it's not in the API response
      sales: [],
      purchases: [],
      isStripeAccountEnable: user.isStripeAccountEnable
    };
    setSelectedUser( userDetails );
    setDialogType( 'view' );
    setOpenDialog( true );
  };

  const handleEditUser = ( user: User ) => {
    const userDetails: UserDetails = {
      ...user,
      countryCode: user.countryCode || '',
      phoneNumber: user.phoneNumber || '',
      name: `${ user.firstName } ${ user.lastName }`,
      role: 'user', // Default role since it's not in the API response
      sales: [],
      purchases: [],

    };
    setSelectedUser( userDetails );
    setDialogType( 'edit' );
    setOpenDialog( true );
    // Set the current user status in editForm
    setEditForm( {
      password: '',
      status: user.status as 'active' | 'inactive'
    } );
  };

  const [ editForm, setEditForm ] = useState( {
    password: '',
    status: 'active' as 'active' | 'inactive'
  } );

  const handleEditFormChange = ( field: string, value: string ) => {
    setEditForm( prev => ( {
      ...prev,
      [ field ]: value
    } ) );
  };

  const handleSaveEdit = async () => {
    if ( !selectedUser ) return;

    try {
      setLoading( true );

      // Prepare update data - only password and status
      const updateData: any = {
        status: editForm.status
      };

      // Only include password if it's not empty
      if ( editForm.password.trim() ) {
        updateData.password = editForm.password;
      }

      const response = await userService.updateUser( selectedUser.id.toString(), updateData );

      if ( response.success ) {
        // Update the user in the local state
        setUsers( users.map( u =>
          u.id === selectedUser.id
            ? { ...u, status: editForm.status as 'active' | 'inactive' }
            : u
        ) );
        setAllUsers( allUsers.map( u =>
          u.id === selectedUser.id
            ? { ...u, status: editForm.status as 'active' | 'inactive' }
            : u
        ) );
        setToast( {
          open: true,
          message: response.message || 'User updated successfully',
          severity: 'success'
        } );
        handleCloseDialog();
        // Reset form
        setEditForm( { password: '', status: 'active' } );
      } else {
        setToast( {
          open: true,
          message: response.message || 'Failed to update user',
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Failed to update user:', error );
      setToast( {
        open: true,
        message: 'Failed to update user',
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleDeleteUser = ( user: User ) => {
    const userDetails: UserDetails = {
      ...user,
      countryCode: user.countryCode || '',
      phoneNumber: user.phoneNumber || '',
      name: `${ user.firstName } ${ user.lastName }`,
      role: 'user', // Default role since it's not in the API response
      sales: [],
      purchases: []
    };
    setSelectedUser( userDetails );
    setDialogType( 'delete' );
    setOpenDialog( true );
  };

  const handleViewSales = ( user: User ) => {
    navigate( `/sales?userId=${ user.id }` );
  };

  const handleViewPurchases = ( user: User ) => {
    navigate( `/purchases?userId=${ user.id }` );
  };

  const handleCloseDialog = () => {
    setOpenDialog( false );
    // setSelectedUser(null);
    setDialogType( null );
  };

  const handleConfirmDelete = async () => {
    if ( selectedUser ) {
      setIsDeleting( true );
      try {
        const response = await userService.deleteUser( { userId: selectedUser.id.toString(), status: "delete" } );
        if ( response.success ) {
          setToast( {
            open: true,
            message: response.message,
            severity: 'success'
          } );
          handleCloseDialog();
          // Refresh the user list from the server
          fetchUsers( page, rowsPerPage, searchTerm );
        } else {
          setToast( {
            open: true,
            message: response.message || t.users.delete.error,
            severity: 'error'
          } );
        }
      } catch ( error ) {
        console.error( 'Failed to delete user:', error );
        setToast( {
          open: true,
          message: t.users.delete.error,
          severity: 'error'
        } );
      } finally {
        setIsDeleting( false );
      }
    }
  };

  const handleChangePage = ( event: unknown, newPage: number ) => {

    setPage( newPage );
    fetchUsers( newPage, rowsPerPage, searchTerm );
  };

  const handleChangeRowsPerPage = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const newRowsPerPage = parseInt( event.target.value, 10 );

    setRowsPerPage( newRowsPerPage );
    setPage( 0 );
    fetchUsers( 0, newRowsPerPage, searchTerm );
  };

  const handleStatusClick = ( user: User ) => {
    setUserToUpdate( user );
    setStatusDialogOpen( true );
  };

  const handleStatusConfirm = async () => {
    if ( userToUpdate ) {
      await handleToggleStatus( userToUpdate );
      setStatusDialogOpen( false );
      setUserToUpdate( null );
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen( false );
    setUserToUpdate( null );
  };

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };
  const filteredUsers = users.filter( user => statusFilter === 'all' ? true : user.status === statusFilter );
  if ( loading && users.length === 0 ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading users...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box sx={ { mb: 4 } }>
          { toast.open && (
            <Snackbar
              open={ toast.open }
              autoHideDuration={ 6000 }
              onClose={ handleCloseToast }
              anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
            >
              <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
                { toast.message }
              </Alert>
            </Snackbar>
          ) }
          {/* <Typography variant="h4" gutterBottom>
          { t.users.management.title }
        </Typography> */}
          <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
            <Box sx={ {
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            } }>
              <PeopleIcon sx={ { fontSize: 28 } } />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                { t.users.management.title }
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage and monitor your users
              </Typography>
            </Box>

          </Stack>
          <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 } }>
            <Chip
              icon={ <ReceiptIcon /> }
              label={ `${ totalUsers } users` }
              color="primary"
              variant="outlined"
            />
          </Box>

          <Grid container spacing={ 2 } sx={ { mb: 3 } }>
            <Grid item xs={ 12 } md={ 6 }>
              <TextField
                fullWidth
                // label={ t.common.search }
                value={ searchTerm }
                onChange={ handleSearchChange }
                InputProps={ {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                } }
                sx={ {
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                  }
                } }
                placeholder="Search by first name, last name, or email..."
              />
            </Grid>

            <Grid item xs={ 12 } md={ 6 }>
              <FormControl fullWidth>
                <InputLabel>{ t.common.status }</InputLabel>
                <Select
                  value={ statusFilter }
                  label={ t.common.status }
                  onChange={ handleStatusChange }
                >
                  <MenuItem value="all">{ t.users.management.all }</MenuItem>
                  <MenuItem value="active">{ t.users.management.active }</MenuItem>
                  <MenuItem value="inactive">{ t.users.management.inactive }</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Paper>
            { filteredUsers.length === 0 ? (

              <Box sx={ {
                textAlign: 'center',
                py: 8,
                color: 'text.secondary'
              } }>
                <OrderIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
                <Typography variant="h6" sx={ { mb: 1 } }>
                  No user found
                </Typography>
                <Typography variant="body2">
                  { searchTerm ? 'Try adjusting your search criteria' : 'No user available' }
                </Typography>
              </Box>

            ) : ( <>
              <TableContainer
                component={ Paper }
                sx={ {
                  borderRadius: 3,
                  overflow: "hidden",

                } }
              >
                <Table>
                  <TableHead>
                    <TableRow sx={ { backgroundColor: 'primary.main' } }>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "ID" }
                      </TableCell>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "FTRST NAME" }
                      </TableCell>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "LAST NAME" }
                      </TableCell>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "EMAIL" }
                      </TableCell>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "STATUS" }
                      </TableCell>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "LOGIN TYPE" }
                      </TableCell>
                      <TableCell align="center" sx={ {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      } }>
                        { "ACTIONS" }
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { loading ? (
                      <TableRow>
                        <TableCell colSpan={ 5 } align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={ 5 } align="center">
                          { t.users.management.noUsers }
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map( ( user, idx ) => (
                        <TableRow key={ user.id }>
                          <TableCell align="center">{ user.id }</TableCell>
                          <TableCell align="center">{ `${ capitalize( user.firstName ) } ` || "N/A" }</TableCell>
                          <TableCell align="center">{ capitalize( user.lastName ) || "N/A" }</TableCell>
                          <TableCell align="center">{ user.email || "N/A" }</TableCell>
                          <TableCell align="center">

                            {/* If user is deleted → show "Deleted" and disable */ }
                            { user.deletedAt ? (
                              <Button
                                variant="text"
                                size="small"
                                color="error"
                                disabled
                                sx={ {
                                  textTransform: "none",
                                  fontWeight: "bold",
                                  opacity: 2.9
                                } }
                              >
                                DELETED
                              </Button>
                            ) : (
                              /* Else show active/inactive & make clickable */
                              <Chip
                                label={ user.status === "active"
                                  ? t.users.management.active
                                  : t.users.management.inactive
                                }
                                color={ user.status === "active" ? "success" : "error" }
                                size="small"
                                onClick={ () => handleStatusClick( user ) }
                                clickable={ !loading }
                                sx={ {
                                  fontWeight: "bold",
                                  cursor: loading ? "not-allowed" : "pointer",
                                  opacity: loading ? 0.6 : 1,
                                  textTransform: "none"
                                } }
                              />

                            ) }

                          </TableCell>

                          <TableCell align="center">{ capitalize( user.loginType ) }</TableCell>
                          <TableCell align="center">
                            <Box sx={ { display: 'flex', gap: 1, justifyContent: 'center' } }>

                              {/* View */ }
                              <Tooltip title={ t.common.view }>
                                <IconButton onClick={ () => handleViewUser( user ) } size="small" color="primary">
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>

                              {/* Edit */ }
                              { user?.deletedAt === null && (
                                <Tooltip title={ t.common.edit }>
                                  <IconButton onClick={ () => handleEditUser( user ) } size="small" color="primary">
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              ) }

                              {/* Sales */ }
                              <Tooltip title={ t.users.sales.title }>
                                <IconButton onClick={ () => handleViewSales( user ) } size="small" color="primary">
                                  <StorefrontIcon />
                                </IconButton>
                              </Tooltip>

                              {/* Purchases */ }
                              <Tooltip title={ t.users.purchases.title }>
                                <IconButton onClick={ () => handleViewPurchases( user ) } size="small" color="primary">
                                  <ShoppingCartIcon />
                                </IconButton>
                              </Tooltip>

                              {/* Delete only if user is NOT deleted */ }
                              { user?.deletedAt === null && (
                                <Tooltip title={ t.common.delete }>
                                  <IconButton onClick={ () => handleDeleteUser( user ) } size="small" color="error">
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              ) }

                            </Box>
                          </TableCell>

                        </TableRow>
                      ) )
                    ) }
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={ [ 5, 10, 25 ] }
                component="div"
                count={ totalUsers }
                rowsPerPage={ rowsPerPage }
                page={ page }
                onPageChange={ handleChangePage }
                onRowsPerPageChange={ handleChangeRowsPerPage }
              />
            </> ) }

          </Paper>

          {/* Status Change Confirmation Dialog */ }
          <Dialog
            open={ statusDialogOpen }
            onClose={ handleStatusCancel }
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={ {
                bgcolor: userToUpdate?.status === 'active' ? 'error.main' : 'success.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                textTransform: 'none',
                gap: 2,
                py: 2.5,
                px: 3
              } }
            >
              { userToUpdate?.status === 'active'
                ? "Deactivate User"  // You can update name in locales
                : "Active User" }
            </DialogTitle>

            <DialogContent style={ { paddingTop: "20px" } }>
              <Typography sx={ { fontSize: '1rem' } }>
                { userToUpdate && (
                  userToUpdate.status === 'active'
                    ? `${ t.users.management.toggleStatusConfirm.deactivate } ${ userToUpdate.firstName } ${ userToUpdate.lastName }?`
                    : `${ t.users.management.toggleStatusConfirm.activate } ${ userToUpdate.firstName } ${ userToUpdate.lastName }?`
                ) }
              </Typography>
            </DialogContent>

            <DialogActions sx={ { p: 3, gap: 2 } }>
              <Button
                onClick={ handleStatusCancel }
                disabled={ loading }
                variant="outlined"
                sx={ {
                  minWidth: 100,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                } }
              >
                { t.common.cancel }
              </Button>

              <Button
                onClick={ handleStatusConfirm }
                variant="contained"
                disabled={ loading }
                sx={ {
                  minWidth: 120,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: userToUpdate?.status === 'active' ? 'error.main' : 'success.main',
                  '&:hover': {
                    bgcolor: userToUpdate?.status === 'active' ? 'error.dark' : 'success.dark'
                  }
                } }
                startIcon={ loading ? <CircularProgress size={ 20 } /> : null }
              >
                { loading
                  ? t.common.saving
                  : userToUpdate?.status === 'active'
                    ? "Deactivate "
                    : "Active" }
              </Button>
            </DialogActions>
          </Dialog>

          {/* View User Dialog */ }
          <Dialog
            open={ openDialog && dialogType === 'view' }
            onClose={ handleCloseDialog }
            maxWidth="md"
            fullWidth
            PaperProps={ {
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              }
            } }
          >
            <DialogTitle sx={ {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.3rem',
              textAlign: 'center',
              py: 2
            } }>
              { t.users.management.userDetails }
            </DialogTitle>
            <DialogContent sx={ { p: 0 } }>
              { selectedUser && (
                <Box>
                  {/* Header with Profile Image and Basic Info */ }
                  <Box sx={ {
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    p: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                  } }>
                    {/* Profile Image */ }
                    <Box sx={ {
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    } }>
                      { selectedUser.image ? (
                        <img
                          src={ selectedUser.image }
                          alt="Profile"
                          style={ {
                            width: '100px',
                            height: '100px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '4px solid white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          } }
                        />
                      ) : (
                        <Box sx={ {
                          width: '100px',
                          height: '100px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '4px solid white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        } }>
                          <Typography variant="h3" color="white" sx={ { fontWeight: 600 } }>
                            { selectedUser.firstName.charAt( 0 ) }{ selectedUser.lastName.charAt( 0 ) }
                          </Typography>
                        </Box>
                      ) }

                    </Box>

                    {/* User Basic Info */ }
                    <Box sx={ { flex: 1 } }>
                      <Typography variant="h4" sx={ {
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 1
                      } }>
                        { selectedUser.firstName } { selectedUser.lastName }
                      </Typography>
                      <Typography variant="body1" sx={ {
                        color: 'text.secondary',
                        mb: 2,
                        fontSize: '1.1rem'
                      } }>
                        { selectedUser.email }
                      </Typography>
                      <Chip
                        label={ selectedUser.status === 'active' ? t.users.management.active : t.users.management.inactive }
                        color={ selectedUser.status === 'active' ? 'success' : 'error' }
                        variant="filled"
                        size="medium"
                        sx={ {
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          px: 2,
                          py: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        } }
                      />
                    </Box>
                  </Box>

                  {/* Detailed Information */ }
                  <Box sx={ { p: 4 } }>
                    <Typography variant="h6" sx={ {
                      mb: 3,
                      color: 'text.primary',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    } }>
                      <Box component="span" sx={ {
                        width: 4,
                        height: 20,
                        backgroundColor: 'primary.main',
                        borderRadius: 1
                      } } />
                      Contact Information
                    </Typography>

                    <Grid container spacing={ 3 }>
                      <Grid item xs={ 12 } md={ 6 }>
                        <Box sx={ {
                          p: 3,
                          backgroundColor: 'grey.50',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'grey.100',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }
                        } }>
                          <Typography variant="subtitle2" color="textSecondary" sx={ {
                            mb: 1,
                            fontWeight: 600,
                            fontSize: '0.875rem',

                            letterSpacing: '0.5px'
                          } }>
                            Phone Number
                          </Typography>
                          <Typography variant="body1" sx={ {
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            color: 'text.primary'
                          } }>
                            { selectedUser.countryCode ? `${ selectedUser.countryCode }-${ selectedUser.phoneNumber }` : 'N/A' }
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={ 12 } md={ 6 }>
                        <Box sx={ {
                          p: 3,
                          backgroundColor: 'grey.50',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'grey.100',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }
                        } }>
                          <Typography variant="subtitle2" color="textSecondary" sx={ {
                            mb: 1,
                            fontWeight: 600,
                            fontSize: '0.875rem',

                            letterSpacing: '0.5px'
                          } }>
                            Account Status
                          </Typography>
                          <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                            <Box sx={ {
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: selectedUser.status === 'active' ? '#4caf50' : '#f44336'
                            } } />
                            <Typography variant="body1" sx={ {
                              fontWeight: 500,
                              fontSize: '1.1rem',
                              color: 'text.primary',
                              textTransform: 'capitalize'
                            } }>
                              { selectedUser.status }
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Additional Info Section */ }
                    <Box sx={ { mt: 4 } }>
                      <Typography variant="h6" sx={ {
                        mb: 3,
                        color: 'text.primary',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      } }>
                        <Box component="span" sx={ {
                          width: 4,
                          height: 20,
                          backgroundColor: 'primary.main',
                          borderRadius: 1
                        } } />
                        Account Details
                      </Typography>

                      <Grid container spacing={ 3 }>
                        { selectedUser.deletedAt && (
                          <Grid item xs={ 12 } md={ 6 }>
                            <Box sx={ {
                              p: 3,
                              backgroundColor: 'grey.50',
                              borderRadius: 3,
                              border: '1px solid',
                              borderColor: 'grey.200',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'grey.100',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                              }
                            } }>
                              <Typography variant="subtitle2" color="textSecondary" sx={ {
                                mb: 1,
                                fontWeight: 600,
                                fontSize: '0.875rem',

                                letterSpacing: '0.5px'
                              } }>
                                Deleted On
                              </Typography>

                              <Typography variant="body1" sx={ {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                                color: 'error.main' // red color for deleted hint
                              } }>
                                { new Date( selectedUser.deletedAt ).toLocaleDateString( 'en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                } ) }
                              </Typography>
                            </Box>
                          </Grid>
                        ) }

                        <Grid item xs={ 12 } md={ 6 }>
                          <Box sx={ {
                            p: 3,
                            backgroundColor: 'grey.50',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          } }>
                            <Typography variant="subtitle2" color="textSecondary" sx={ {
                              mb: 1,
                              fontWeight: 600,
                              fontSize: '0.875rem',

                              letterSpacing: '0.5px'
                            } }>
                              Member Since
                            </Typography>
                            <Typography variant="body1" sx={ {
                              fontWeight: 500,
                              fontSize: '1.1rem',
                              color: 'text.primary'
                            } }>
                              { new Date( selectedUser.createdAt ).toLocaleDateString( 'en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              } ) }
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={ 12 } md={ 6 }>
                          <Box sx={ {
                            p: 3,
                            backgroundColor: 'grey.50',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          } }>
                            <Typography variant="subtitle2" color="textSecondary" sx={ {
                              mb: 1,
                              fontWeight: 600,
                              fontSize: '0.875rem',

                              letterSpacing: '0.5px'
                            } }>
                              Last Updated
                            </Typography>
                            <Typography variant="body1" sx={ {
                              fontWeight: 500,
                              fontSize: '1.1rem',
                              color: 'text.primary'
                            } }>
                              { new Date( selectedUser.updatedAt ).toLocaleDateString( 'en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              } ) }
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={ 12 } md={ 6 }>
                          <Box sx={ {
                            p: 3,
                            backgroundColor: 'grey.50',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          } }>
                            <Typography variant="subtitle2" color="textSecondary" sx={ {
                              mb: 1,
                              fontWeight: 600,
                              fontSize: '0.875rem',

                              letterSpacing: '0.5px'
                            } }>
                              Stripe Account status
                            </Typography>
                            <Box sx={ { display: 'flex', flexDirection: 'column', gap: 0.5 } }>

                              { selectedUser.isStripeAccountEnable && (
                                <>
                                  {/* ACCOUNT ID */ }
                                  <Typography variant="body2" sx={ { color: 'text.secondary' } }>
                                    <strong>ID:</strong> { selectedUser.stripeAccountId || "N/A" }
                                  </Typography>

                                </>
                              ) }
                              {/* STATUS ROW */ }
                              <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                                <Box
                                  sx={ {
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: selectedUser.isStripeAccountEnable ? 'success.main' : 'error.main'
                                  } }
                                />

                                { selectedUser.isStripeAccountEnable ? "Active" : "Inactive" }

                              </Box>

                            </Box>

                          </Box>
                        </Grid>
                        <Grid item xs={ 12 } md={ 6 }>
                          <Box sx={ {
                            p: 3,
                            backgroundColor: 'grey.50',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          } }>
                            <Typography variant="subtitle2" color="textSecondary" sx={ {
                              mb: 1,
                              fontWeight: 600,
                              fontSize: '0.875rem',

                              letterSpacing: '0.5px'
                            } }>
                              Stripe Customer ID
                            </Typography>
                            <Box sx={ { display: 'flex', flexDirection: 'column', gap: 0.5 } }>

                              <>

                                {/* CUSTOMER ID */ }
                                <Typography variant="body2" sx={ { color: 'text.secondary' } }>
                                  <strong>ID:</strong> { selectedUser.stripeId || "N/A" }
                                </Typography>
                              </>

                            </Box>

                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ) }
            </DialogContent>
            <DialogActions sx={ { p: 3, pt: 1 } }>
              <Button
                onClick={ handleCloseDialog }
                variant="outlined"
                sx={ {
                  minWidth: 120,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                } }
              >
                { t.common.close }
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit User Dialog */ }
          <Dialog
            open={ openDialog && dialogType === 'edit' }
            onClose={ handleCloseDialog }
            maxWidth="md"
            fullWidth
            PaperProps={ {
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              }
            } }
          >
            <DialogTitle sx={ {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.3rem',
              textAlign: 'center',
              py: 2
            } }>
              { t.users.management.editUser }
            </DialogTitle>
            <DialogContent sx={ { p: 4 } }>
              { selectedUser && (
                <Box sx={ { mt: 2 } }>
                  <Box sx={ { mb: 4 } }>
                    <Typography variant="h6" sx={ {
                      mb: 2,
                      color: 'text.primary',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    } }>
                      <Box component="span" sx={ {
                        width: 4,
                        height: 20,
                        backgroundColor: 'primary.main',
                        borderRadius: 1
                      } } />
                      User Information
                    </Typography>
                    <Divider sx={ { opacity: 0.3 } } />
                  </Box>

                  <Grid container spacing={ 3 }>
                    <Grid item xs={ 12 } md={ 6 }>
                      <Typography variant="subtitle2" color="textSecondary" sx={ { mb: 1, fontWeight: 600 } }>
                        First Name
                      </Typography>
                      <Typography variant="body1" sx={ {
                        mb: 2,
                        p: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      } }>
                        { selectedUser.firstName }
                      </Typography>
                    </Grid>
                    <Grid item xs={ 12 } md={ 6 }>
                      <Typography variant="subtitle2" color="textSecondary" sx={ { mb: 1, fontWeight: 600 } }>
                        Last Name
                      </Typography>
                      <Typography variant="body1" sx={ {
                        mb: 2,
                        p: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      } }>
                        { selectedUser.lastName }
                      </Typography>
                    </Grid>
                    <Grid item xs={ 12 } md={ 6 }>
                      <Typography variant="subtitle2" color="textSecondary" sx={ { mb: 1, fontWeight: 600 } }>
                        { t.common.email }
                      </Typography>
                      <Typography variant="body1" sx={ {
                        mb: 2,
                        p: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      } }>
                        { selectedUser.email }
                      </Typography>
                    </Grid>
                    <Grid item xs={ 12 } md={ 6 }>
                      <Typography variant="subtitle2" color="textSecondary" sx={ { mb: 1, fontWeight: 600 } }>
                        { t.common.status }
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={ editForm.status }
                          onChange={ ( e ) => handleEditFormChange( 'status', e.target.value ) }
                          variant="outlined"
                          sx={ {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: 'grey.50',
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: 'primary.main',
                            }
                          } }
                        >
                          <MenuItem value="active">
                            <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                              <Chip
                                label={ t.users.management.active }
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          </MenuItem>
                          <MenuItem value="inactive">
                            <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                              <Chip
                                label={ t.users.management.inactive }
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              ) }
            </DialogContent>
            <DialogActions sx={ { p: 3, pt: 1, gap: 2 } }>
              <Button
                onClick={ handleCloseDialog }
                disabled={ loading }
                variant="outlined"
                sx={ {
                  minWidth: 120,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                } }
              >
                { t.common.cancel }
              </Button>
              <Button
                onClick={ handleSaveEdit }
                variant="contained"
                disabled={ loading }
                startIcon={ loading ? <CircularProgress size={ 20 } /> : null }
                sx={ {
                  minWidth: 120,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                } }
              >
                { loading ? t.common.saving : t.common.save }
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */ }
          <Dialog
            open={ openDialog && dialogType === 'delete' }
            onClose={ handleCloseDialog }
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={ {
              bgcolor: 'error.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 2.5,
              px: 3
            } }>
              <DeleteIcon />
              Delete User
            </DialogTitle>
            <DialogContent style={ { paddingTop: "20px" } }>
              <Typography>
                { selectedUser && (
                  `${ t.users.delete.confirm } ${ selectedUser.firstName } ${ selectedUser.lastName }?`
                ) }
              </Typography>
            </DialogContent>
            <DialogActions sx={ { p: 3, g: 2 } }>
              <Button onClick={ handleCloseDialog } disabled={ isDeleting } variant="outlined"
                sx={ {
                  minWidth: 100,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                } }>
                { t.common.cancel }
              </Button>
              <Button
                onClick={ handleConfirmDelete }
                color="error"
                variant="contained"
                disabled={ isDeleting }
                sx={ {
                  minWidth: 120,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                } }
                startIcon={ isDeleting ? <CircularProgress size={ 20 } /> : null }
              >
                { isDeleting ? t.common.saving : t.common.delete }
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Slide>
    </Container>
  );
};

export default UserManagement;
